import {
  createStructuredDoczHandle,
  type DoczHandle,
  type DoczModule,
  type StructuredBlock,
} from "@norkron/web-docz";
import {
  type CanvasSurfaceHandle,
  createFragmentSurface,
  DocumentMirror,
  fragmentPageConfig,
  loadPageLayout,
  type PageConfig,
  type PageLayoutModule,
} from "@norkron/web-page-layout";
import { FRAGMENT_PAPER, surfaceInk } from "../../lib/canvasCommentColors";
import { trackFocusSource } from "../../lib/pointerFocus";
import { sharedDoczModule } from "../../lib/doczHandle";
import { blockWithDefaultInk } from "./commentSurfaceModel";
import { CANVAS_EDIT_PAGE_CONFIG } from "../canvas/canvasPageConfig";
import { applyCanvasScale } from "../canvas/canvasPresentation";

/**
 * `quoteSurface` — THE ANCHORED QUOTE ON THE CANVAS ENGINE (owner decision D-E:
 * no DOM renders any docz IR anywhere).
 *
 * ── WHAT WAS WRONG ──────────────────────────────────────────────────────────
 *
 * Row-3 of the comment card is the anchor made visible: it exists to say "this
 * comment is about THAT passage". It rendered `comment.quote` — a PLAIN STRING
 * off the wire (`api/types.ts`) — as a React text child, which
 * `annotations.css` painted at `font-size: 12px; font-style: italic` in the
 * card's type ink on the card's type wash. The passage it names is painted by
 * `pdfz-layout.wasm` in the document's face, at the document's body size, on
 * paper. So the ONE feature whose whole job is identity showed the reviewer two
 * different renderings of the same words, and the reviewer had to take it on
 * trust that they were the same words. That is the anchor model's credibility,
 * spent on a font stack.
 *
 * ── HOW A PLAIN STRING BECOMES SOMETHING THE ENGINE CAN PAINT ───────────────
 *
 * There is no IR on the wire for a quote and there must not be a client-side
 * `fromHtml` (Zig owns the docz IR — RULE ZERO, `lib/doczHandle`). But docz
 * already ships the one factory that needs no transport at all:
 * `createStructuredDoczHandle(module, { blocks })` over a single
 * `{ kind: 'paragraph', runs: [{ text, marks }] }`. That is REAL docz IR built
 * by the docz wasm, not a hand-rolled model — the same handle type the read and
 * edit surfaces mount, one block long. Everything downstream is then the shipped
 * chain, unmodified: `DoczEditBridge` → `makeStructuredEncodeBlocks` →
 * `pl_layout` → `createCanvasSurface`.
 *
 * ── WHY THE CONFIDENCE STATE IS A MARK ──────────────────────────────────────
 *
 * "This quote is not in this document" (orphan / never-anchored) was `<s>` plus
 * `opacity: 0.7`. A canvas has no CSS to strike, so the fact rides as the docz
 * `strikeout` MARK the engine already paints — which is strictly better than the
 * CSS was: the mark also projects into `DocumentMirror` as `<s>`, so the state
 * reaches assistive technology as markup rather than as a colour.
 *
 * ── WHAT A CANVAS QUOTE DOES NOT GIVE YOU ───────────────────────────────────
 *
 * Inherited from `<ReadCanvas>`, and stated for the same reason: browser
 * find-in-page (⌘F) will not find the quote's words, native DOM-range selection
 * across the quote and the body text around it is gone, browser translation and
 * browser text zoom do not reach the glyphs, and print does not paint them.
 * `DocumentMirror` keeps the words readable to assistive technology and keeps
 * them in the DOM (so `.comment-card__quote-text`'s text content is still the
 * quote); it restores none of the above. Selection + copy ARE restored, from the
 * IR rather than from the pixels, by `createCanvasSurface`.
 *
 * ── THERE IS NO PAPER ───────────────────────────────────────────────────────
 *
 * It used to be white by CONSTRAINT: `createCanvasSurface` forwarded `fontFamily`
 * alone into the painter, so `RenderOptions.paper` — which `render.ts` has
 * resolved since the first raster — was unreachable from the mount and a tile
 * could only be an opaque sheet. Then it was white by DECISION ("a quote is a
 * clipping of the page"), and briefly the card's own composited colour.
 *
 * All three were OPAQUE. The owner's order (2026-07-30) is transparent: this
 * surface passes `FRAGMENT_PAPER` and the box paints NOTHING behind its glyphs,
 * so the card's wash — the one `annotations.css` already owns — is what shows.
 * See `lib/canvasCommentColors`'s note for why "matches the card" and "is not
 * painted" are not the same thing even though they photograph identically.
 *
 * ⛔ The INK is still resolved and still passed. Paper and ink were bound
 * together deliberately: the engine's default ink is black, and black on a
 * transparent canvas over a dark card is worse than the white sheet it replaced.
 */

/** The single block id the quote paragraph carries. Stable, so a re-layout of the
 *  same quote addresses the same block. */
export const QUOTE_BLOCK_ID = "q0";

/** The class `mountQuoteSurface` paints into. Exported because the ROW around it
 *  has to know which clicks are already spoken for — see
 *  {@link quoteRegionActivates}. */
export const QUOTE_SURFACE_CLASS = "ela-quote__surface";

/**
 * Does a click on the Row-3 REGION (`.comment-card__quote`) mean "jump"?
 *
 * ## The bug this closes
 *
 * Owner, 2026-07-30: *"the ENTIRE `comment-card__quote` MUST be clickable not just
 * `pl-page` `pl-tile-text` and `comment-card__quote-jump`!"* Only two things
 * responded: the painted canvas (via {@link mountQuoteSurface}'s own listener) and
 * the jump button. The region's 6/10px padding, the gutter left of the type-ink
 * edge, the blank paper right of a short quote (the box is TRIMMED to the ink, so
 * that area is region, not surface) and the whole `text`-state fallback `<p>` were
 * all dead. A reviewer aiming at "the quote" hits those constantly.
 *
 * ## Why it is a predicate and not just `onClick={jump}`
 *
 * Two things inside the region already own their clicks, and firing again would
 * be worse than not firing at all:
 *
 *  · **the painted surface** — its listener carries the DRAG GUARD
 *    (`if (surface.selection()) return`). Without this exclusion a drag-select of
 *    the quote in order to COPY it would bubble a `click` to the region on release
 *    and jump the document away mid-gesture — the exact regression that stopped
 *    Row-3 being one big `<button>` in the first place.
 *  · **real controls** — the jump `<button>` (which is also the version-switch
 *    `PopoverTrigger`, so a second `setSwitchOpen` would fight its toggle and the
 *    popover could never be dismissed) and any link inside the region.
 *
 * Everything else in the box is the reviewer aiming at the quotation.
 */
export function quoteRegionActivates(target: EventTarget | null): boolean {
  // Not an element (a synthetic event with no target, a text node in some
  // engines) — nothing claims it, so the region does.
  if (target === null || !(target as Partial<Element>).closest) return true;
  const el = target as Element;
  // A real control owns its own activation.
  if (el.closest("button, a, [role='button'], input, select, textarea") !== null) {
    return false;
  }
  // The canvas surface owns its own clicks, WITH the drag guard.
  return el.closest(`.${QUOTE_SURFACE_CLASS}`) === null;
}

/**
 * The row width, in CSS px, assumed when the mount cannot be measured (first
 * paint, a `display: none` ancestor, a test DOM with no layout).
 *
 * STATED rather than inherited, and that is the whole point: `columnWidthPtForCss`
 * returns `null` for a 0-width mount, and an ABSENT `PageConfig` resolves upstream
 * to `A4_DEFAULT` — a 595pt sheet with 54pt margins. A comment card would then
 * hold a full page. ~320px is a review-rail card row.
 */
export const QUOTE_FALLBACK_WIDTH_CSS = 320;

/**
 * The docz IR for a plain-string quote: one paragraph, one run.
 *
 * Empty / whitespace-only yields NO blocks. docz's editable floor would otherwise
 * turn an empty quote into an empty paragraph and the card would paint a blank
 * sheet where it should paint nothing at all.
 */
export function quoteBlocks(
  quote: string,
  opts: { readonly struck?: boolean } = {},
): StructuredBlock[] {
  if (quote.trim() === "") return [];
  return [
    {
      id: QUOTE_BLOCK_ID,
      kind: "paragraph",
      runs: [{ text: quote, marks: opts.struck === true ? [{ type: "strikeout" }] : [] }],
    },
  ];
}

/** A live `DoczHandle` over one quote — real docz IR, built by the docz wasm. */
export function createQuoteHandle(
  docz: DoczModule,
  quote: string,
  opts: { readonly struck?: boolean } = {},
): DoczHandle {
  return createStructuredDoczHandle(docz, { blocks: quoteBlocks(quote, opts) });
}

/**
 * The last-resort column, in POINTS, when neither the row's width nor
 * {@link QUOTE_FALLBACK_WIDTH_CSS} can be converted. ~320 CSS px expressed in
 * points, so the two fallbacks agree.
 */
const QUOTE_FALLBACK_WIDTH_PT = 240;

/**
 * The `pl_layout` page template a QUOTE box lays out with.
 *
 * ## `inherit` IS THE WHOLE POINT OF THIS FUNCTION
 *
 * The plumbing every fragment shares — `continuous: true`, four zero margins,
 * `pageWidthPt` from the engine's own `columnWidthPtForCss` with a stated
 * fallback — is upstream in {@link fragmentPageConfig}, because the comment body
 * needed exactly the same three things and a second copy of them was a duplication
 * defect.
 *
 * What is NOT upstream, and must not be, is `inherit`. A quote IS THE DOCUMENT —
 * it is a clipping of the page — so it spreads {@link CANVAS_EDIT_PAGE_CONFIG},
 * the template the read surface, the paginated editor and the branded PDF all
 * share. The face, the body size, the leading and the wrap strategy are therefore
 * the document's BY CONSTRUCTION and cannot drift into a second opinion. A comment
 * body is not the document and passes NO `inherit` at all; `fragmentPageConfig` has
 * no default precisely so that difference stays visible at both call sites.
 *
 * `justify` and `glyphGranular` ride through from the document template and are
 * LOAD-BEARING for the same reason they are there: `forceJustify` is the only wrap
 * strategy pdfz stamps per-line `(block_ord, byte)` provenance on, and without
 * provenance there is no hit-test — i.e. no selecting the quote to copy it.
 *
 * CONSEQUENCE, stated: a 320px card row gives a ~240pt column while the document
 * body has ~451pt, so the quote WRAPS at different points than the passage does.
 * The glyphs, the face, the size and the leading are identical; the line breaks
 * cannot be, in a rail a third of a page wide.
 */
export function quotePageConfig(widthCss: number): PageConfig {
  return fragmentPageConfig({
    widthCss,
    zoom: 1,
    // ⛔ THE INHERIT. A quote is the document; see the note above.
    inherit: CANVAS_EDIT_PAGE_CONFIG,
    fallbackWidthCss: QUOTE_FALLBACK_WIDTH_CSS,
    fallbackWidthPt: QUOTE_FALLBACK_WIDTH_PT,
  });
}

/** The two wasm engines a quote box needs. Both are process-wide singletons. */
export interface QuoteEngines {
  readonly docz: DoczModule;
  readonly engine: PageLayoutModule;
}

/**
 * Resolve the shared engines.
 *
 * THIS IS THE BATCHING STORY'S FOUNDATION. Neither call instantiates anything
 * per quote: `sharedDoczModule()` is the app's already-memoised `docz.wasm` (the
 * SAME instance the editor mounts over — `lib/doczHandle`), and `loadPageLayout()`
 * is `web-page-layout`'s own module-scope cache. N quote boxes make N `layout()`
 * calls into ONE wasm instance and ONE arena; they do not make N instances.
 */
export async function loadQuoteEngines(): Promise<QuoteEngines> {
  const [docz, engine] = await Promise.all([sharedDoczModule(), loadPageLayout()]);
  return { docz, engine };
}

/**
 * Report ONCE when `el` first comes near the viewport. Returns a disposer.
 *
 * THIS IS THE BATCHING CHOICE, and it is VIRTUALISATION rather than pooling —
 * because pooling is not available. A pooled surface can only live in one DOM
 * container at a time (`createCanvasSurface(container, …)` owns that container's
 * children), and a thread's N cards are N separate boxes in a scrolling rail, so
 * one surface cannot serve them. Nor can one layout pass: a single continuous
 * surface holding all N quotes would paint them into ONE canvas, and there is no
 * way to place slices of one canvas into N cards.
 *
 * What is left, and what this does: a rail of 40 notes mounts a surface only for
 * the handful of cards the reviewer has actually scrolled to. The cost of an
 * unmounted card is one `<p>` of text. `rootMarginPx` pre-warms just off-screen so
 * a scroll does not reveal an unpainted box.
 *
 * It is ONE-SHOT (`disconnect()` on first intersection) and never tears a painted
 * box back down: re-entering a card would otherwise re-run the layout and flash a
 * repaint on every scroll reversal. The live set is therefore bounded by "cards
 * the reviewer looked at", not by "cards that exist".
 *
 * Where `IntersectionObserver` is absent (Node/Bun unit env) it reports visible
 * immediately, so a test DOM with no layout paints rather than hanging on an
 * observer that can never fire.
 */
export type ObserveVisible = (el: HTMLElement, onVisible: () => void) => () => void;

export function observeVisibleDefault(rootMarginPx = 240): ObserveVisible {
  return (el, onVisible) => {
    const Ctor = (globalThis as { IntersectionObserver?: typeof IntersectionObserver })
      .IntersectionObserver;
    if (typeof Ctor !== "function") {
      onVisible();
      return () => {};
    }
    // LATCHED, not merely disconnected. `disconnect()` stops future observations
    // but does NOT cancel a callback already queued as a task, so a box that came
    // into view during the same frame can be reported twice — which would re-enter
    // the mount effect and run the wasm layout again. The latch is what makes
    // "once" true rather than likely.
    let done = false;
    const io = new Ctor(
      (entries) => {
        if (done || !entries.some((e) => e.isIntersecting)) return;
        done = true;
        io.disconnect();
        onVisible();
      },
      { rootMargin: `${rootMarginPx}px` },
    );
    io.observe(el);
    return () => {
      done = true;
      io.disconnect();
    };
  };
}

export interface MountQuoteOptions {
  /** The element the surface and the mirror are mounted into. Its children are
   *  REPLACED by the surface, so it must carry no React children. */
  readonly mount: HTMLElement;
  readonly quote: string;
  /** The anchor is not in this document (orphan / never anchored). */
  readonly struck?: boolean;
  /** Accessible name for the mirror's `role="document"` landmark. */
  readonly label?: string;
  /** BCP-47 language of the quoted text (WCAG 3.1.2). */
  readonly lang?: string;
  readonly testId?: string;
  readonly dpr?: number;
  /** Fired on a pointer click that is NOT the end of a drag-select. */
  readonly onActivate?: () => void;
  /**
   * The row width the quote must fill, in CSS px. A LIVE getter — it is re-read on
   * every layout pass, which is what makes `refresh()` after a rail resize pick up
   * the new column with no second surface.
   *
   * Defaults to the mount's own `clientWidth`, and a caller should override it with
   * something that is ALWAYS laid out. Measured bug: the mount is the element the
   * surface owns, and `<QuoteCanvas>` hides it until the first paint — so reading
   * the width off it returned 0 at exactly the moment the template needed it, and
   * the quote painted a 320px sheet in a 539px card row.
   */
  readonly widthCss?: () => number;
}

/**
 * Where the painted INK actually ends, in CSS px.
 *
 * The sheet is as wide as the card row, but a short quote fills a fraction of it
 * and the rest is blank paper. Left unclipped, the box reads as a wide empty white
 * field — a disabled text input, not a quotation (observed in a real browser on
 * `/w/wettformat/article/1`: 36 characters of ink in a 539px sheet). Clipping to
 * the ink is LOSSLESS because the text is left-set: with `justify: true` a
 * paragraph's last line is never stretched, so the space to the right of the
 * longest line is paper and nothing else.
 */
export function inkWidthCss(surface: CanvasSurfaceHandle): number {
  const view = surface.core.view;
  if (view.pageCount === 0) return 0;
  const pg = view.page(0);
  let maxPt = 0;
  for (let i = 0; i < pg.runCount; i++) {
    const run = view.run(pg.runFirst + i);
    const right = run.xPt + run.widthPt;
    if (right > maxPt) maxPt = right;
  }
  return maxPt * (96 / 72) * surface.plan.zoom;
}

export interface MountedQuote {
  readonly surface: CanvasSurfaceHandle;
  readonly mirror: DocumentMirror;
  readonly handle: DoczHandle;
  /** The painted sheet, in CSS px — what the reviewer's eye measures. */
  readonly paintedWidthCss: number;
  readonly paintedHeightCss: number;
  /** Where the words end, in CSS px — the box is trimmed to this. */
  readonly inkWidthCss: number;
  /** The scale in force. Resolved through `canvasPresentation`, so it is the
   *  app-wide policy's answer and never this surface's own opinion. */
  readonly scale: number;
  /** Re-lay-out at the mount's CURRENT width (a rail resize). */
  refresh(): void;
  destroy(): void;
}

/**
 * Mount one quote box. Returns `null` when there is nothing to paint.
 *
 * ## What is upstream now, and what is still this file's
 *
 * The five steps every fragment mount needs — structured docz handle →
 * `DoczEditBridge` → `createWasmRelayout` with a LIVE config getter →
 * `createCanvasSurface` at `interactivity: 'read'` → the accessible mirror — are
 * `createFragmentSurface`, because the comment body spelled out the same five and
 * a second copy was a duplication defect. The app's SCALE POLICY is injected into
 * it rather than decided by it: `canvasPresentation` is elaborate's one answer to
 * "how big is a document on screen", and a library must not hold an opinion there.
 *
 * What is still quote-specific, and why each one is:
 *
 *  · `scroller: null` — the host drives the painted window. NOT a
 *    micro-optimisation: left to auto-detect, EVERY mounted quote box would
 *    install a capture-phase scroll listener and re-read its rect on every scroll
 *    of the review rail. A rail of 40 notes would then do 40 rect reads and 40
 *    tile-window recomputes per scroll event. A quote box is one small fully
 *    painted tile, so there is no window to track.
 *  · `align: 'start'` — the sheet is PINNED LEFT. The painter centres by default,
 *    which is right for a document in a pane and wrong here: `fitToInk` below
 *    trims the box to the ink width, and a centred sheet inside a narrower box is
 *    clipped on the LEFT, cutting the first words off. This used to be a
 *    `margin: 0 !important` in `quoteCanvas.css` fighting an inline style, with a
 *    comment saying the clean fix was an upstream option that did not exist. It
 *    exists; the `!important` is gone.
 *  · `fitToInk` / {@link inkWidthCss} — a short quote in a wide row reads as a
 *    disabled text input unless the blank paper is trimmed.
 *  · the click-to-jump handler, and no highlight plan (nothing inside a quote
 *    carries a comment of its own).
 *
 * ## No activatable band, and that is deliberate
 *
 * The keyboard route to the anchor is `.comment-card__quote-jump`, a REAL
 * `<button>` with a real accessible name (see `QuoteCanvas.tsx`). Adding an
 * `activatable` band over the same words would put a SECOND tab stop on one
 * action — a WCAG 2.4.3 regression dressed up as an improvement. The painted words
 * stay clickable for pointer users; the button is the keyboard path.
 */
export function mountQuoteSurface(
  deps: QuoteEngines,
  opts: MountQuoteOptions,
): MountedQuote | null {
  const raw = quoteBlocks(opts.quote, { struck: opts.struck });
  if (raw.length === 0) return null;

  // ⛔ NO SHEET AT ALL — `FRAGMENT_PAPER`, owner order 2026-07-30. Two opaque
  // answers preceded it: export white (measured on `/w/wettformat/article/1`,
  // dark theme, as `rgba(255, 255, 255, 255)` inside a `rgb(28, 31, 36)` card)
  // and then the card's own composited colour, which merely LOOKED like no fill.
  // The quote box now paints nothing behind its glyphs and the card's CSS is the
  // one owner of that colour.
  //
  // ⛔ The INK does NOT go away with the paper — see `surfaceInk`. The engine's
  // default is black, and black on a transparent canvas over a dark card is a
  // perfect model nobody can read, so `null` (an unreadable cascade) is the only
  // case that passes no ink.
  const ink = surfaceInk(opts.mount);
  const blocks =
    ink === null ? raw : raw.map((b) => blockWithDefaultInk(b, ink));

  const measureWidth = opts.widthCss ?? (() => opts.mount.clientWidth);

  const fragment = createFragmentSurface({
    host: opts.mount,
    docz: deps.docz,
    engine: deps.engine,
    blocks,
    paper: FRAGMENT_PAPER,
    // A GETTER, not a value: upstream re-reads it on every layout pass, so a
    // `refresh()` after a rail resize picks up the new row width with no second
    // surface and no second measurement pass of our own.
    pageConfig: () => quotePageConfig(measureWidth()),
    zoom: 1,
    ...(opts.dpr !== undefined ? { dpr: opts.dpr } : {}),
    maxDpr: 2,
    // The host owns the painted window — see the note above.
    scroller: null,
    // PINNED LEFT — see the note above. This replaced a `!important`.
    align: "start",
    colors: {
      // No caret exists on a read surface (it does not even create the caret
      // canvas), so this is inert; stated rather than defaulted so it never
      // implies a caret is coming.
      caret: "transparent",
      // Concrete colours only — `fillStyle` cannot parse `var(--x)` and the
      // assignment is a SILENT no-op (see `lib/canvasCommentColors`). The
      // selection tint is the browser-neutral blue: a quote box is one short
      // fragment and does not carry the document's comment palette.
      selection: "rgba(37, 99, 235, 0.3)",
      highlight: "rgba(37, 99, 235, 0.18)",
    },
    // The accessible projection — IR-derived, painter-agnostic, and the ONLY
    // reason a canvas quote is readable at all. `exposure: 'exclusive'` is
    // upstream's default for a fragment and load-bearing here: the mirror's own
    // default is `'silent'` (present but `aria-hidden'), which is right when it
    // shadows a surface that is already accessible HTML. This surface is a
    // `<canvas>` with NO accessibility tree, so a silent mirror would leave the
    // quote with no representation whatsoever. It also keeps the words in the DOM,
    // which is why `.comment-card__quote-text` still has the quote as its text.
    mirror: {
      ...(opts.label !== undefined ? { label: opts.label } : {}),
      ...(opts.lang !== undefined ? { lang: opts.lang } : {}),
      ...(opts.testId !== undefined ? { testId: opts.testId } : {}),
    },
    // THE SCALE. Resolved through the ONE presentation policy every canvas
    // document surface in this app shares. A private fit-to-width here is exactly
    // the defect `canvasPresentation` exists to make impossible: it would paint
    // the quote's type at a different size from the passage's, which is the bug
    // this whole component is fixing, reintroduced one layer down.
    applyScale: (host, surf, zoom) => applyCanvasScale(host, surf, zoom),
  });
  if (!fragment) return null;

  const { surface, handle, scale } = fragment;
  const mirror = fragment.mirror;
  if (!mirror) throw new Error("quoteSurface: the accessible mirror is mandatory");

  // Click → jump, unless a selection is live. A click that ENDED A DRAG is a
  // copy gesture, not a navigate gesture; without this guard, selecting the quote
  // to copy it would also scroll the document out from under the reviewer. Same
  // guard, same reason, as `<ReadCanvas>`'s comment-click handler.
  const onClick = (): void => {
    if (surface.selection()) return;
    opts.onActivate?.();
  };
  opts.mount.addEventListener("click", onClick);

  /**
   * Trim the trailing blank paper — but ONLY when the words fit the row.
   *
   * Three cases, and the third is why the row width is a condition rather than
   * decoration. The engine clamps a column at 144pt (192 CSS px), so a rail
   * narrower than that gets a sheet WIDER than the card. Pinning the box to the ink
   * there would set a width bigger than the row with `overflow-x: hidden` — and the
   * words past the card edge would be unreachable, because nothing between the box
   * and the card scrolls. So:
   *
   *  · ink < sheet AND ink fits the row → clip to the ink (blank paper, nothing else);
   *  · ink fills the column             → nothing to clip;
   *  · ink wider than the row           → leave it full-width so the base rule's
   *    `overflow-x: auto` lets the reviewer pan. Never rescale — that is
   *    `canvasPresentation`'s standing policy and it holds on this surface too.
   */
  const fitToInk = (): void => {
    const ink = inkWidthCss(surface);
    const sheet = surface.plan.documentWidthCss;
    const rowCss = measureWidth();
    if (ink > 0 && ink < sheet && ink <= rowCss) {
      opts.mount.style.width = `${Math.ceil(ink)}px`;
      opts.mount.style.overflowX = "hidden";
    } else {
      opts.mount.style.removeProperty("width");
      opts.mount.style.removeProperty("overflow-x");
    }
  };
  fitToInk();

  // WHICH MODALITY FOCUSED THIS SURFACE. The engine focuses its container from
  // `pointerdown` (it must, for ⌘A/⌘C), which Chromium scores as `:focus-visible`
  // — so a plain mouse click on a quote painted a focus ring. See
  // `lib/pointerFocus`; `quoteCanvas.css` suppresses only the pointer case.
  const untrackFocus = trackFocusSource(opts.mount);

  let destroyed = false;
  return {
    surface,
    mirror,
    handle,
    paintedWidthCss: surface.plan.documentWidthCss,
    paintedHeightCss: surface.plan.documentHeightCss,
    inkWidthCss: inkWidthCss(surface),
    scale,
    refresh: () => {
      if (destroyed) return;
      // Upstream re-lays-out through the live page-config getter AND re-enforces
      // the mirror's exposure — a re-layout can re-plan tiles, i.e. mint NEW
      // `<canvas>` elements the `'exclusive'` pass never saw, and without that
      // re-enforcement "the painted sibling is hidden and the mirror is the
      // representation" holds only at mount.
      fragment.refresh();
      // The ink moves with the column, so the trim is recomputed, not cached.
      fitToInk();
    },
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      opts.mount.removeEventListener("click", onClick);
      untrackFocus();
      fragment.destroy();
    },
  };
}
